import { foldText } from '../utils/textSearch.js';

/**
 * GuideBookSearchService - Handles search and filtering logic
 * Filters guide/faq categories based on search queries
 * Matching ignores case and accents ("zolty" finds "Żółty")
 *
 * @class GuideBookSearchService
 */
class GuideBookSearchService {
    constructor(cacheService) {
        /**
         * Reference to cache service for full-text searching
         * @type {GuideBookCacheService}
         */
        this.cacheService = cacheService;

        /**
         * Minimum search query length required for search
         * @type {number}
         */
        this.MIN_SEARCH_LENGTH = 3;
    }

    /**
     * Filter categories by search query
     * Matches against category titles, descriptions, and content
     * 
     * @param {Array} categories - Category items to search
     * @param {string} query - Search query
     * @param {Function} titleExtractor - Function to extract title from category (default: item.title)
     * @param {Function} descExtractor - Function to extract description from category (default: item.description)
     * @param {Function} contentExtractor - Function to extract content from category (default: item.content)
     * @returns {Array} Filtered categories matching the query
     */
    filterCategories(categories, query, titleExtractor, descExtractor, contentExtractor) {
        // Return all if query is empty or too short
        if (!query || query.trim().length < this.MIN_SEARCH_LENGTH) {
            return categories;
        }

        const searchTerm = foldText(query.trim());

        return categories.filter(item => {
            const title = titleExtractor ? titleExtractor(item) : (item.title || '');
            const desc = descExtractor ? descExtractor(item) : (item.description || '');

            // Search in title and description first (most relevant)
            if (foldText(title).includes(searchTerm) ||
                foldText(desc).includes(searchTerm)) {
                return true;
            }

            // Search in content using cache service's strip HTML method
            if (contentExtractor) {
                const content = contentExtractor(item);
                if (content) {
                    const plainText = foldText(this.cacheService.stripHtmlTags(content));
                    if (plainText.includes(searchTerm)) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    /**
     * Search content by title and description only (fast, surface-level search)
     * Useful for category lists where content extractor isn't available
     * 
     * @param {Array} categories - Items to search
     * @param {string} query - Search query
     * @param {Function} titleExtractor - Function to extract title
     * @param {Function} descExtractor - Function to extract description
     * @returns {Array} Filtered categories
     */
    searchShallow(categories, query, titleExtractor, descExtractor) {
        return this.filterCategories(categories, query, titleExtractor, descExtractor, null);
    }

    /**
     * Search content including full-text search of content
     * More thorough but slower
     * 
     * @param {Array} categories - Items to search
     * @param {string} query - Search query
     * @param {Function} titleExtractor - Function to extract title
     * @param {Function} descExtractor - Function to extract description
     * @param {Function} contentExtractor - Function to extract content
     * @returns {Array} Filtered categories
     */
    searchDeep(categories, query, titleExtractor, descExtractor, contentExtractor) {
        return this.filterCategories(categories, query, titleExtractor, descExtractor, contentExtractor);
    }

    /**
     * Rank search results by relevance
     * Titles match more relevant than descriptions, which are more relevant than content
     * 
     * @param {Array} categories - Items to rank
     * @param {string} query - Search query
     * @param {Function} titleExtractor - Function to extract title
     * @param {Function} descExtractor - Function to extract description
     * @param {Function} contentExtractor - Function to extract content
     * @returns {Array} Ranked categories (sorted by relevance)
     */
    rankByRelevance(categories, query, titleExtractor, descExtractor, contentExtractor) {
        const searchTerm = foldText(query.trim());

        const scored = categories.map(item => {
            let score = 0;

            // Title match: highest weight
            const title = foldText(titleExtractor ? titleExtractor(item) : (item.title || ''));
            if (title.includes(searchTerm)) {
                score += 3;
                // Bonus if term is at start of title
                if (title.indexOf(searchTerm) === 0) {
                    score += 2;
                }
            }

            // Description match: medium weight
            const desc = foldText(descExtractor ? descExtractor(item) : (item.description || ''));
            if (desc.includes(searchTerm)) {
                score += 2;
            }

            // Content match: lowest weight
            if (contentExtractor) {
                const content = contentExtractor(item);
                if (content) {
                    const plainText = foldText(this.cacheService.stripHtmlTags(content));
                    if (plainText.includes(searchTerm)) {
                        score += 1;
                    }
                }
            }

            return { item, score };
        });

        // Sort by score descending, maintaining original order for equal scores
        return scored
            .sort((a, b) => b.score - a.score)
            .map(({ item }) => item);
    }
}

export default GuideBookSearchService;
